import React, { useEffect, useState, useContext } from 'react';  
import { useNavigate, useParams } from "react-router-dom";
import Axios from "axios";
import { useImmerReducer } from 'use-immer';
import { Grid, Typography, IconButton, Button, CircularProgress, Breadcrumbs, Link, Box, Container, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import "leaflet/dist/leaflet.css";
import StateContext from "../../Contexts/StateContext";
import { useTranslation } from 'react-i18next';

// Assets
import defaultProfilePicture from "../../Assets/defaultProfilePicture.jpg";
import PhoneIcon from '@mui/icons-material/Phone';
import RoomIcon from '@mui/icons-material/Room';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Utils
import { formatPhoneNumber } from "../../Utils/PhoneUtils";

function MarketplaceAccommodationDetail() {
    const { t, i18n } = useTranslation();
    const { config, environment } = require('../../index');
    const navigate = useNavigate();
    const GlobalState = useContext(StateContext);
    const params = useParams();
    const [currentPicture, setCurrentPicture] = useState(0);

    const customIcon = new L.Icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',  
        iconSize: [25, 41],  
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
    });

    const initialState = {   
        dataIsLoading: true,  
        accInfo: null,
        sellerProfileInfo: null,
        openDeleteDialog: false,
        deleteBtnDisabled: false,
    };

    function ReducerFunction(draft, action){
        switch(action.type){
            case 'catchAccInfo':
                draft.accInfo = action.accObject;
                break;
            case 'catchSellerProfileInfo':  
                draft.sellerProfileInfo = action.profileObject;
                break;
            case 'toggleDeleteDialog':
                draft.openDeleteDialog = action.value;
                break;
            case 'setDeleteBtnDisabled':
                draft.deleteBtnDisabled = action.value;
                break;
            case 'loadingDone': 
                draft.dataIsLoading = false;
                break; 
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

    async function handleDeleteListing() {
        dispatch({ type: 'setDeleteBtnDisabled', value: true });
        try {
            const token = GlobalState.jwtAccessToken || localStorage.getItem('jwtAccessToken');
            await Axios.delete(`${config[environment].marketplaceAccommodationEndpoint}${params.id}/delete/`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            navigate(`/${i18n.language}/all-accommodation-listings`);
        } catch (e) {
            console.error("Error deleting accommodation:", e);
            dispatch({ type: 'setDeleteBtnDisabled', value: false });
            dispatch({ type: 'toggleDeleteDialog', value: false });
        }
    }

    useEffect(() => {
        const source = Axios.CancelToken.source();
        async function getAccInfo() {
            try {
                const endpoint = `${config[environment].marketplaceAccommodationEndpoint}${params.id}/`;
                const response = await Axios.get(endpoint, { cancelToken: source.token });
                console.log("Acc info received:", response.data);
                dispatch({ type: 'catchAccInfo', accObject: response.data });
                
                // After getting accommodation info, fetch seller profile
                if (response.data.seller) {
                    const profileEndpoint = config[environment].getProfileEndpoint(response.data.seller);
                    const profileRes = await Axios.get(profileEndpoint, { cancelToken: source.token });
                    dispatch({ type: 'catchSellerProfileInfo', profileObject: profileRes.data });
                }
                
                dispatch({ type: 'loadingDone' });
            } catch (e) {
                if (!Axios.isCancel(e)) {
                    console.error("Error fetching accommodation detail:", e);
                    dispatch({ type: 'loadingDone' });
                }
            }
        }
        getAccInfo();
        return () => source.cancel();
    }, [params.id, config, environment]);

    if (state.dataIsLoading) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ height: "100vh" }}>
                <CircularProgress />
            </Grid>
        );
    }

    if (!state.accInfo) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h5">{t('Market.NotFound', 'Not found')}</Typography>
                <Button onClick={() => navigate(`/${i18n.language}/accommodation-market`)} sx={{ mt: 2 }}>{t('Common.Back')}</Button>
            </Container>
        );
    }

    const listingPictures = [state.accInfo.picture1, state.accInfo.picture2, state.accInfo.picture3]
        .filter(picture => picture !== null);

    const date = new Date(state.accInfo.date_posted);
    const formattedDate = `${date.getFullYear()} - ${date.getMonth() + 1} - ${date.getDate()}`;

    return (
        <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mt: '10px' }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate(`/${i18n.language}/`)} sx={{ cursor: 'pointer' }}>
                        {t('Header.Kezdolap')}
                    </Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate(`/${i18n.language}/szallasterkep`)} sx={{ cursor: 'pointer' }}>
                        {t('Header.Szallas')}
                    </Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate(`/${i18n.language}/all-accommodation-listings?type=${state.accInfo.accommodation_type}`)} sx={{ cursor: 'pointer' }}>
                        {t(`SubMenu.${state.accInfo.accommodation_type}`)}
                    </Link>
                    <Typography color="text.primary">{state.accInfo.title}</Typography>
                </Breadcrumbs>

                {GlobalState.userIsLogged && (GlobalState.userId == state.accInfo.seller || GlobalState.userUsername === 'supergiant') && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            size="small"
                            onClick={() => navigate(`/${i18n.language}/marketplace-accommodation/${params.id}/edit`)}
                        >
                            {t('Common.Edit')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<DeleteIcon />}
                            color="error"
                            size="small"
                            onClick={() => dispatch({ type: 'toggleDeleteDialog', value: true })}
                        >
                            {t('Common.Delete')}
                        </Button>
                    </Box>
                )}
            </Box>

            <Grid container spacing={4}>
                {/* Left side: Images and Description */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ width: '100%', height: { xs: '300px', md: '500px' }, bgcolor: '#eee', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                        {listingPictures.length > 0 ? (
                            <img 
                                src={listingPictures[currentPicture]} 
                                alt={state.accInfo.title} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="text.secondary">{t('Common.NoImage')}</Typography>
                            </Box>
                        )}
                    </Box>
                    
                    {listingPictures.length > 1 && (
                        <Grid container spacing={1} sx={{ mb: 4 }}>
                            {listingPictures.map((pic, idx) => (
                                <Grid item key={idx} xs={4}>
                                    <Box 
                                        onClick={() => setCurrentPicture(idx)}
                                        sx={{ 
                                            height: '100px', 
                                            cursor: 'pointer', 
                                            opacity: currentPicture === idx ? 1 : 0.6,
                                            border: currentPicture === idx ? '2px solid #105b79' : 'none',
                                            borderRadius: 1,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img src={pic} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    <Typography variant="h4" gutterBottom sx={{ color: '#105b79', fontWeight: 'bold' }}>
                        {state.accInfo.title}
                    </Typography>
                    
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
                        <RoomIcon sx={{ mr: 1 }} /> {t(`SubMenu.${state.accInfo.accommodation_type}`)}
                    </Typography>

                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 4 }}>
                        {state.accInfo.description}
                    </Typography>

                </Grid>

                {/* Price, Contact and Map */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ p: 3, border: '1px solid #ddd', borderRadius: 2, mb: 3, textAlign: 'center', bgcolor: '#f9f9f9' }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'green', mb: 1 }}>
                            {(state.accInfo.price_per_night || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} RON
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {state.accInfo.price_type === 'Person' ? t('Accommodation.PricePerNightPerson') : t('Accommodation.PricePerNightRoom')}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {t('Common.PostedOn', 'Posted on')}: {formattedDate}
                        </Typography>
                    </Box>

                    {(state.accInfo.rooms || state.accInfo.capacity) && (
                        <Box sx={{ p: 3, border: '1px solid #ddd', borderRadius: 2, mb: 3, bgcolor: '#fff' }}>
                            <Grid container spacing={2}>
                                {state.accInfo.rooms && (
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">{t('Academy.Rooms')}</Typography>
                                        <Typography variant="h6">{state.accInfo.rooms}</Typography>
                                    </Grid>
                                )}
                                {state.accInfo.capacity && (
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">{t('Business.Capacity')}</Typography>
                                        <Typography variant="h6">{state.accInfo.capacity} {t('Common.Person', 'fő')}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {state.sellerProfileInfo && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={12}>
                                <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, textAlign: 'center', bgcolor: '#fff', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'left', gap: 2 }}>
                                        <Box 
                                            component="img" 
                                            src={state.sellerProfileInfo.profile_picture || defaultProfilePicture} 
                                            sx={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '1px solid #105b79', cursor: 'pointer' }}
                                            onClick={() => navigate(`/${i18n.language}/institutions/${state.sellerProfileInfo.seller}`)}
                                        />
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#105b79', lineHeight: 1.2 }}>
                                                {state.sellerProfileInfo.company_name}
                                            </Typography>
                                            <Link 
                                                component="button"
                                                variant="caption"
                                                onClick={() => navigate(`/${i18n.language}/institutions/${state.sellerProfileInfo.seller}`)}
                                                sx={{ color: 'text.secondary', textDecoration: 'underline' }}
                                            >
                                                {t('Common.ViewProfile', 'Profil megtekintése')}
                                            </Link>
                                        </Box>
                                    </Box>
                                    <Button 
                                        variant="contained" 
                                        size="small"
                                        startIcon={<PhoneIcon />} 
                                        fullWidth
                                        sx={{ mt: 2, bgcolor: '#105b79', '&:hover': { bgcolor: '#0d4a63' } }}
                                        href={`tel:${state.accInfo.phone_number || state.sellerProfileInfo.phone_number}`}
                                    >
                                        {formatPhoneNumber(state.accInfo.phone_number || state.sellerProfileInfo.phone_number)}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={12}>
                                <Box sx={{ height: '200px', width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                                    <MapContainer center={[state.accInfo.latitude, state.accInfo.longitude]} zoom={15} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url={config[environment].osmTiles}
                                        />
                                        <Marker position={[state.accInfo.latitude, state.accInfo.longitude]} icon={customIcon}>
                                            <Popup>{state.accInfo.title}</Popup>
                                        </Marker>
                                    </MapContainer>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
            <Dialog
                open={state.openDeleteDialog}
                onClose={() => dispatch({ type: 'toggleDeleteDialog', value: false })}
            >
                <DialogTitle>{t('Accommodation.DeleteTitle', 'Szálláshely törlése')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Accommodation.DeleteConfirm', 'Biztosan törölni szeretné ezt a szálláshelyet? Ez a művelet nem vonható vissza.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch({ type: 'toggleDeleteDialog', value: false })} disabled={state.deleteBtnDisabled}>
                        {t('Common.Cancel')}
                    </Button>
                    <Button onClick={handleDeleteListing} color="error" variant="contained" disabled={state.deleteBtnDisabled}>
                        {state.deleteBtnDisabled ? <CircularProgress size={24} /> : t('Common.Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default MarketplaceAccommodationDetail;
